import { useAdminContext } from 'components/admin/context';
import { usePopulationCount } from 'components/admin/dashboard/hooks';
import { useAuthContext } from 'components/auth';
import { useToggle } from 'lib/hooks';
import { CompanySize } from 'lib/types/company';
import _ from 'lodash';
import { ReactNode, useState } from 'react';
import { getCompanySizeFromLearnerCount } from './helpers';
import { SalesRequestContext } from './SalesRequestContext';
import { SalesRequestModal } from './SalesRequestModal';

interface ISalesRequestProviderProps {
  children: ReactNode;
}

/**
 * Wraps the logic for handling sales requests throughout Admin Hub.
 *
 * Small companies (<50 employees) should not be able to book a sales call.
 */
export const SalesRequestContextProvider = ({
  children,
}: ISalesRequestProviderProps) => {
  const { session } = useAuthContext();
  const { isFreemiumCompany } = useAdminContext();

  const [interest, setInterest] = useState<string>();
  const [companySize, setCompanySize] = useState<CompanySize>();
  const [isSalesRequestModalOpen, toggleSalesRequestModal] = useToggle();

  const { data: learnerCount } = usePopulationCount();

  const getCompanySize = () => {
    if (companySize) {
      return companySize;
    }
    // Freemium customers: use reported `companySize`.
    if (isFreemiumCompany) {
      return session.company.size;
    }
    // If company has learners: use learner count to determine size.
    if (_.isNumber(learnerCount)) {
      return getCompanySizeFromLearnerCount(learnerCount);
    }
    // If company has no learners: use reported `companySize`.
    if (session.company.size) {
      return session.company.size;
    }
    // Otherwise: default to 1-10.
    return CompanySize.ONE_TO_TEN;
  };

  const computedCompanySize = getCompanySize();

  return (
    <SalesRequestContext.Provider
      value={{
        companySize: computedCompanySize,
        interest,
        isSalesRequestModalOpen,
        setInterest,
        setCompanySize,
        toggleSalesRequestModal,
      }}
    >
      {children}
      <SalesRequestModal
        interest={interest}
        isVisible={isSalesRequestModalOpen}
        onHide={toggleSalesRequestModal}
      />
    </SalesRequestContext.Provider>
  );
};
